import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { FcmNotificationType } from '@/modules/notifications';

import { NOTIFICATION_TYPE_CONFIG } from '../constants/notification-type-config';
import { useNotifications } from './use-notifications';

const TOAST_DURATION_MS = 8000;

/**
 * Shows Sonner toast notifications for incoming FCM push messages.
 *
 * Subscribes to NOTIFICATION_CREATED events and derives display text
 * from notificationType + NOTIFICATION_TYPE_CONFIG i18n keys.
 * Must be mounted once in AuthenticatedTemplate.
 */
export const useToastNotifications = (): void => {
  const { t } = useTranslation('authenticated.notifications');
  const navigate = useNavigate();

  useNotifications(FcmNotificationType.NOTIFICATION_CREATED, notification => {
    if (notification.silent) return;

    const { data } = notification;
    const notificationType = data.notificationType as string | undefined;
    const argsRaw = data.args as string | undefined;
    const args: Record<string, string> = argsRaw ? JSON.parse(argsRaw) : {};
    const actionArgsRaw = data.actionArgs as string | undefined;
    const actionArgs = actionArgsRaw ? JSON.parse(actionArgsRaw) : null;

    const typeConfig = notificationType ? NOTIFICATION_TYPE_CONFIG[notificationType] : null;
    if (!typeConfig) return;

    const title = t(typeConfig.titleKey, args);
    const description = t(typeConfig.descriptionKey, args);
    const actionUrl = typeConfig.getActionUrl ? typeConfig.getActionUrl(actionArgs ?? {}) : null;

    toast(title, {
      description,
      icon: <Bell className="h-4 w-4" />,
      duration: TOAST_DURATION_MS,
      position: 'top-right',
      action: actionUrl
        ? {
            label: 'View',
            onClick: () => navigate(actionUrl),
          }
        : undefined,
    });
  });
};
